import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Plus, Trash2, Gift, Pencil, Clock } from 'lucide-react';
import type { Reward } from '../lib/provider/types';

export const ParentRewards: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [isAdding, setIsAdding] = useState(false);
    const [editingReward, setEditingReward] = useState<Reward | null>(null);

    // Form state
    const [title, setTitle] = useState('');
    const [cost, setCost] = useState(500);

    const { data: rewards, isLoading } = useQuery({
        queryKey: ['rewards', session.user?.household_id],
        queryFn: () => provider.getRewards(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    // Sort rewards by cost_points from lower to higher
    const sortedRewards = React.useMemo(() => {
        if (!rewards) return [];
        return [...rewards].sort((a, b) => a.cost_points - b.cost_points);
    }, [rewards]);

    const createReward = useMutation({
        mutationFn: (newReward: Omit<Reward, 'id' | 'created_at'>) => provider.createReward(newReward),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rewards'] });
            setIsAdding(false);
            setEditingReward(null);
            setTitle('');
            setCost(500);
        }
    });

    const updateReward = useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Reward> }) => provider.updateReward(id, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rewards'] });
            setIsAdding(false);
            setEditingReward(null);
            setTitle('');
            setCost(500);
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to update reward');
        },
    });

    const deleteReward = useMutation({
        mutationFn: (id: string) => provider.deleteReward(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['rewards'] });
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to delete reward');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingReward) {
            updateReward.mutate({
                id: editingReward.id,
                updates: {
                    title,
                    cost_points: cost,
                },
            });
            return;
        }

        createReward.mutate({
            household_id: session.user!.household_id,
            title,
            description: '',
            cost_points: cost,
            active: true,
            created_by: session.user!.id,
        });
    };

    if (isLoading) return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-slate-900">{t.parentRewards.title}</h1>
                    <Link 
                        to="/parent/rewards/history" 
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-primary-200 rounded-lg text-primary-600 font-bold hover:bg-primary-50 transition-colors"
                    >
                        <Clock size={16} />
                        {t.parentRewards.history}
                    </Link>
                </div>
                <button
                    onClick={() => {
                        if (isAdding) {
                            setIsAdding(false);
                            setEditingReward(null);
                            setTitle('');
                            setCost(500);
                            return;
                        }

                        setIsAdding(true);
                        setEditingReward(null);
                        setTitle('');
                        setCost(500);
                    }}
                    className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                    <Plus size={20} />
                    {t.parentRewards.createReward}
                </button>
            </div>

            {isAdding && (
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-4">
                    <h2 className="text-lg font-bold mb-4">{editingReward ? 'Edit Reward' : t.parentRewards.newReward}</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t.parentRewards.rewardName}</label>
                            <input
                                type="text"
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Extra 30 mins Screen Time"
                                className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t.parentRewards.costPoints}</label>
                            <input
                                type="number"
                                required
                                value={cost}
                                onChange={(e) => setCost(Number(e.target.value))}
                                className="w-full px-4 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsAdding(false);
                                    setEditingReward(null);
                                    setTitle('');
                                    setCost(500);
                                }}
                                className="px-4 py-2 text-slate-600 hover:text-slate-800"
                            >
                                {t.common.cancel}
                            </button>
                            <button
                                type="submit"
                                className="bg-primary-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-primary-700 transition-colors"
                            >
                                {t.parentRewards.saveReward}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedRewards?.length === 0 ? (
                    <div className="col-span-full bg-white rounded-xl p-12 border border-slate-200 text-center">
                        <p className="text-slate-500">{t.parentRewards.noRewards}</p>
                    </div>
                ) : (
                    sortedRewards?.map((reward) => (
                        <div key={reward.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm flex flex-col items-center text-center relative group">
                            <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 mb-4">
                                <Gift size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 mb-1">{reward.title}</h3>
                            <div className="text-primary-600 font-black text-2xl mb-2">{reward.cost_points} {t.common.pts}</div>
                            {reward.creator_name && (
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-4">{t.parentQuests.createdBy} {reward.creator_name}</div>
                            )}
                            <div className="mt-auto flex items-center gap-4">
                                <button
                                    className="flex items-center gap-2 text-slate-400 hover:text-primary-600 transition-colors"
                                    onClick={() => {
                                        setIsAdding(true);
                                        setEditingReward(reward);
                                        setTitle(reward.title);
                                        setCost(reward.cost_points);
                                    }}
                                >
                                    <Pencil size={18} />
                                    Edit
                                </button>
                                <button
                                    className="flex items-center gap-2 text-slate-400 hover:text-red-500 transition-colors"
                                    onClick={() => {
                                        if (confirm('Are you sure you want to delete this reward?')) {
                                            deleteReward.mutate(reward.id);
                                        }
                                    }}
                                >
                                    <Trash2 size={18} />
                                    {t.common.delete}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
